import {
  ChangeDetectionStrategy,
  Component,
  effect,
  OnDestroy,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectionPositionPair,
} from '@angular/cdk/overlay';
import { DropdownContentUIComponent } from './dropdown-content/dropdown-content.component';
import { animationFrameScheduler, auditTime, fromEvent, Subscription } from 'rxjs';

@Component({
  selector: 'fr8bot-ui-dropdown',
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, DropdownContentUIComponent],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownUIComponent implements OnDestroy {
  private readonly connectedOverlay = viewChild.required(CdkConnectedOverlay);
  private readonly defaultOrigin = viewChild.required(CdkOverlayOrigin);

  readonly isOpen = input(false);
  readonly origin = input<CdkOverlayOrigin | null>(null);

  /** Fires when the dropdown opens itself, not when the host opens it through `isOpen`. */
  readonly opened = output<void>();

  dropdownPositions: ConnectionPositionPair[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  // The host opens it through `isOpen`; the dropdown also opens and closes itself.
  public readonly isOpened = linkedSignal(() => this.isOpen());

  private scrollSubscription?: Subscription;
  private originObserver?: IntersectionObserver;
  private originResizeObserver?: ResizeObserver;

  constructor() {
    effect(() => (this.isOpened() ? this.trackOriginWhileOpen() : this.stopTrackingOrigin()));
  }

  ngOnDestroy() {
    this.stopTrackingOrigin();
  }

  private trackOriginWhileOpen(): void {
    this.stopTrackingOrigin();

    const originElement = (this.origin() ?? this.defaultOrigin()).elementRef.nativeElement;

    this.scrollSubscription = fromEvent(document, 'scroll', { capture: true, passive: true })
      .pipe(auditTime(0, animationFrameScheduler))
      .subscribe(() => this.connectedOverlay().overlayRef?.updatePosition());

    this.originObserver = new IntersectionObserver(([entry]: IntersectionObserverEntry[]) => {
      if (entry && !entry.isIntersecting) {
        this.close();
      }
    });

    this.originObserver.observe(originElement);

    // Sized from an observer rather than `cdkConnectedOverlayWidth` or `(attach)`: both read the
    // origin's layout mid-change-detection, and the forced style recalc makes transitions elsewhere
    // on the page play. It reports before the first paint, and again whenever the origin resizes.
    this.originResizeObserver = new ResizeObserver(() => this.matchOriginWidth(originElement));
    this.originResizeObserver.observe(originElement);
  }

  private stopTrackingOrigin(): void {
    this.scrollSubscription?.unsubscribe();
    this.scrollSubscription = undefined;
    this.originObserver?.disconnect();
    this.originObserver = undefined;
    this.originResizeObserver?.disconnect();
    this.originResizeObserver = undefined;
  }

  private matchOriginWidth(origin: HTMLElement): void {
    const overlayRef = this.connectedOverlay().overlayRef;

    overlayRef?.updateSize({ width: origin.clientWidth });
    overlayRef?.updatePosition();
  }

  public open(): void {
    this.setOpened(true);
  }

  public close(): void {
    this.isOpened.set(false);
  }

  protected onInteractionAreaClick(event: Event): void {
    event.preventDefault();
    this.setOpened(!this.isOpened());
  }

  protected onOutsideClick(): void {
    this.isOpened.set(false);
  }

  private setOpened(isOpened: boolean): void {
    const wasOpened = this.isOpened();
    this.isOpened.set(isOpened);

    if (isOpened && !wasOpened) {
      this.opened.emit();
    }
  }
}
